import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { r, s } from '../../theme/tokens';
import { useMediaQuery, useTheme } from './hooks';
import type { Theme } from '@mui/material/styles';
import type { TabProps as MuiTabProps } from '@mui/material/Tab';
import type { TabsProps as MuiTabsProps } from '@mui/material/Tabs';
import type { ReactElement, SyntheticEvent } from 'react';

export type TabsProps = Omit<MuiTabsProps, 'value' | 'onChange'> & {
  scrollableOnMobile?: boolean;
  preserveScrollOnChange?: boolean;
  value?: string | number;
  onChange?: (event: SyntheticEvent, value: string | number) => void;
};

export const Tabs = ({
  scrollableOnMobile = true,
  preserveScrollOnChange = true,
  variant,
  sx,
  onChange,
  ...rest
}: TabsProps): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const effectiveVariant = scrollableOnMobile && isMobile ? 'scrollable' : variant;
  const handleChange = (event: SyntheticEvent, value: string | number): void => {
    if (!preserveScrollOnChange) {
      onChange?.(event, value);
      return;
    }
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    onChange?.(event, value);
    if (scrollY === null) return;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      });
    } else {
      window.setTimeout(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      }, 0);
    }
  };
  const mergedSx = [
    {
      minHeight: 40,
      color: 'text.primary',
      '& .MuiTabs-flexContainer': { gap: s('sm') },
      '& .MuiTabs-indicator': {
        backgroundColor: 'text.primary',
        height: '1px',
        transition: 'none',
      },
    },
    sx,
  ].filter(Boolean) as MuiTabsProps['sx'];

  return <MuiTabs variant={effectiveVariant} sx={mergedSx} onChange={handleChange} {...rest} />;
};

export type TabProps = MuiTabProps;

export const Tab = ({ sx, ...rest }: TabProps): ReactElement => {
  const mergedSx = [
    {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
      minHeight: 40,
      height: 40,
      paddingTop: 0,
      paddingBottom: 0,
      paddingX: s('md'),
      borderRadius: r('pill'),
      color: 'text.primary',
      '&.Mui-selected': { color: 'text.primary' },
    },
    sx,
  ].filter(Boolean) as MuiTabProps['sx'];

  return <MuiTab sx={mergedSx} {...rest} />;
};
